import { useAxios } from './useAxios'
import { getBackendErrorCode, getBackendErrorField } from './useAxiosError'
import { mailEntitlementSilentStatuses } from './useMailBilling'
import { MailCatchall, MailSendLimitStatus } from '@/types/dashboard/mail'
import type {
  MailAccount,
  CreateMailAccountRequest,
  UpdateMailAccountRequest,
  ChangePasswordRequest,
  Vacation,
  UpdateVacationRequest,
  LoginHistoryResponse,
  Identity,
  IdentityListResponse,
  CreateIdentityRequest,
  AppPasswordListResponse,
  CreateAppPasswordRequest,
  CreateAppPasswordResponse,
  MailDnsStatus,
  MailProvisionResponse,
  UpdateCatchallRequest,
  MailStats,
  MailServerHealth,
  MailQueueStatus,
  MailServerStats,
  MessageLogResponse,
  MessageStatsResponse,
  AccountsSummaryResponse,
  MailSubdomainListResponse,
  SubdomainProvisionResult,
  SubdomainDnsResponse,
  SubdomainCatchAllResponse,
  SetSubdomainCatchAllRequest,
  MailVerifyResult
} from '@/types/dashboard/mail'

const ADDRESS_HOLDER_KEYS: Record<string, string> = {
  mailbox: 'mailbox',
  admin: 'mailbox',
  domain_admin: 'mailbox',
  group: 'shared',
  alias: 'alias',
  list: 'list'
}

function isAddressTaken(error: unknown): boolean {
  return getBackendErrorCode(error) === 'address_taken'
}

/**
 * Statuses of a mail create refusal to keep out of Sentry: a taken address and
 * the entitlement refusals. Pass as `silentStatuses` to handleAxiosError.
 */
export function mailCreateSilentStatuses(error: unknown): number[] {
  return isAddressTaken(error) ? [409] : mailEntitlementSilentStatuses(error)
}

/**
 * The message for an `address_taken` refusal, naming what holds the address
 * when the backend does, or undefined for any other error.
 */
export function addressTakenMessage(t: (key: string) => string, error: unknown): string | undefined {
  if (!isAddressTaken(error)) return undefined
  const leaf = ADDRESS_HOLDER_KEYS[getBackendErrorField<string>(error, 'held_by') ?? '']
  if (!leaf) return t('dashboard.mail.addressTaken.generic')
  const message = t(`dashboard.mail.addressTaken.${leaf}`)
  if (getBackendErrorField<boolean>(error, 'active') === false) {
    return `${message} ${t('dashboard.mail.addressTaken.switchedOff')}`
  }
  return message
}

export function useMail() {
  const listAccounts = (domainId?: number, search?: string, filterType?: string) => {
    const params = new URLSearchParams()
    if (domainId) params.set('domain_id', String(domainId))
    if (search) params.set('search', search)
    if (filterType) params.set('type', filterType)
    const qs = params.toString()
    return useAxios<never, MailAccount[]>('GET', `/mail${qs ? '?' + qs : ''}`, true)
  }

  const getAccount = (id: number) => {
    return useAxios<never, MailAccount>('GET', `/mail/${id}`, true)
  }

  const createAccount = (data: CreateMailAccountRequest) => {
    return useAxios<CreateMailAccountRequest, MailAccount>('POST', '/mail', true, data)
  }

  const updateAccount = (id: number, data: UpdateMailAccountRequest) => {
    return useAxios<UpdateMailAccountRequest, MailAccount>('POST', `/mail/${id}`, true, data)
  }

  const deleteAccount = (id: number) => {
    return useAxios<never, void>('DELETE', `/mail/${id}`, true)
  }

  const changePassword = (id: number, data: ChangePasswordRequest) => {
    return useAxios<ChangePasswordRequest, void>('POST', `/mail/${id}/password`, true, data)
  }

  const toggleActive = (id: number, active: boolean) => {
    return updateAccount(id, { active })
  }

  return {
    listAccounts,
    getAccount,
    createAccount,
    updateAccount,
    deleteAccount,
    changePassword,
    toggleActive
  }
}

export function useMailVacation() {
  const getVacation = (mailId: number) => {
    return useAxios<never, Vacation>('GET', `/mail/${mailId}/vacation`, true)
  }

  const updateVacation = (mailId: number, data: UpdateVacationRequest) => {
    return useAxios<UpdateVacationRequest, Vacation>('PATCH', `/mail/${mailId}/vacation`, true, data)
  }

  return {
    getVacation,
    updateVacation
  }
}

export function useMailIdentities() {
  const listIdentities = (mailId: number) => {
    return useAxios<never, IdentityListResponse>('GET', `/mail/${mailId}/identity`, true)
  }

  const createIdentity = (mailId: number, data: CreateIdentityRequest) => {
    return useAxios<CreateIdentityRequest, Identity>('POST', `/mail/${mailId}/identity`, true, data)
  }

  const deleteIdentity = (mailId: number, identityId: string) => {
    return useAxios<never, void>('DELETE', `/mail/${mailId}/identity/${identityId}`, true)
  }

  return {
    listIdentities,
    createIdentity,
    deleteIdentity
  }
}

export function useMailAppPasswords() {
  const listAppPasswords = (mailId: number) => {
    return useAxios<never, AppPasswordListResponse>('GET', `/mail/${mailId}/app-password`, true)
  }

  const createAppPassword = (mailId: number, data: CreateAppPasswordRequest) => {
    return useAxios<CreateAppPasswordRequest, CreateAppPasswordResponse>(
      'POST',
      `/mail/${mailId}/app-password`,
      true,
      data
    )
  }

  const deleteAppPassword = (mailId: number, passwordId: number) => {
    return useAxios<never, void>('DELETE', `/mail/${mailId}/app-password/${passwordId}`, true)
  }

  return {
    listAppPasswords,
    createAppPassword,
    deleteAppPassword
  }
}

export function useMailLoginHistory() {
  const getLoginHistory = (mailId: number, limit = 50) => {
    return useAxios<never, LoginHistoryResponse>('GET', `/mail/${mailId}/logins?limit=${limit}`, true)
  }

  return {
    getLoginHistory
  }
}

export function useDomainMail() {
  const getDnsStatus = (domainId: string) => {
    return useAxios<never, MailDnsStatus>('GET', `/domain/${domainId}/mail/dns`, true)
  }

  const provisionMail = (domainId: string) => {
    return useAxios<{ enable: boolean }, MailProvisionResponse>(
      'POST',
      `/domain/${domainId}/mail/provision`,
      true,
      { enable: true }
    )
  }

  const verifyMailDomain = (domainId: string) => {
    return useAxios<never, MailVerifyResult>('POST', `/domain/${domainId}/mail/verify`, true)
  }

  const autoConfigureDns = (domainId: string) => {
    return useAxios<never, { message: string }>('POST', `/domain/${domainId}/mail/dns`, true)
  }

  const getCatchall = (domainId: string) => {
    return useAxios<never, MailCatchall>(
      'GET',
      `/domain/${domainId}/mail/catchall`,
      true,
      undefined,
      MailCatchall
    )
  }

  // The PATCH only answers with a status message, so the resulting configuration
  // is read back instead of being inferred client-side.
  const setCatchall = async (domainId: string, targetMailboxId: number) => {
    await useAxios<UpdateCatchallRequest, { message: string }>(
      'PATCH',
      `/domain/${domainId}/mail/catchall`,
      true,
      { target_mailbox_id: targetMailboxId }
    )
    const res = await getCatchall(domainId)
    return res.data
  }

  const disableCatchall = (domainId: string) => {
    return useAxios<never, void>('DELETE', `/domain/${domainId}/mail/catchall`, true)
  }

  const getStats = (domainId: string, period: 'day' | 'week' | 'month' = 'week') => {
    return useAxios<never, MailStats>('GET', `/domain/${domainId}/mail/stats?period=${period}`, true)
  }

  return {
    getDnsStatus,
    provisionMail,
    verifyMailDomain,
    autoConfigureDns,
    getCatchall,
    setCatchall,
    disableCatchall,
    getStats
  }
}

export function useAdminMail() {
  const getHealth = () => {
    return useAxios<never, MailServerHealth>('GET', '/admin/mail/health', true)
  }

  const getQueue = () => {
    return useAxios<never, MailQueueStatus>('GET', '/admin/mail/queue', true)
  }

  const getStats = (period: 'day' | 'week' | 'month' = 'week') => {
    return useAxios<never, MailServerStats>('GET', `/admin/mail/stats?period=${period}`, true)
  }

  const getMessages = (params?: {
    limit?: number
    offset?: number
    domain?: string
    direction?: string
    spam_result?: string
    search?: string
    from?: string
    to?: string
  }) => {
    const query = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') query.set(k, String(v))
      })
    }
    const qs = query.toString()
    return useAxios<never, MessageLogResponse>('GET', `/admin/mail/messages${qs ? '?' + qs : ''}`, true)
  }

  const getMessageStats = (period: 'day' | 'week' | 'month' = 'week') => {
    return useAxios<never, MessageStatsResponse>('GET', `/admin/mail/messages/stats?period=${period}`, true)
  }

  const getAccountsSummary = () => {
    return useAxios<never, AccountsSummaryResponse>('GET', '/admin/mail/accounts/summary', true)
  }

  const listAllAccounts = (params?: {
    limit?: number
    offset?: number
    search?: string
    type?: string
    active?: string
  }) => {
    const query = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') query.set(k, String(v))
      })
    }
    const qs = query.toString()
    return useAxios<never, { accounts: MailAccount[]; total: number }>(
      'GET',
      `/admin/mail/accounts${qs ? '?' + qs : ''}`,
      true
    )
  }

  return {
    getHealth,
    getQueue,
    getStats,
    getMessages,
    getMessageStats,
    getAccountsSummary,
    listAllAccounts
  }
}

export function useSubdomainMail() {
  const listSubdomains = (domainId: string) => {
    return useAxios<never, MailSubdomainListResponse>(
      'GET',
      `/domain/${domainId}/mail/subdomains`,
      true
    )
  }

  const provisionSubdomain = (domainId: string, name: string) => {
    return useAxios<never, SubdomainProvisionResult>(
      'POST',
      `/domain/${domainId}/subdomain/${name}/mail/provision`,
      true
    )
  }

  const getSubdomainDns = (domainId: string, name: string) => {
    return useAxios<never, SubdomainDnsResponse>(
      'GET',
      `/domain/${domainId}/subdomain/${name}/mail/dns`,
      true
    )
  }

  const autoConfigureSubdomainDns = (domainId: string, name: string) => {
    return useAxios<never, void>(
      'POST',
      `/domain/${domainId}/subdomain/${name}/mail/dns`,
      true
    )
  }

  const getSubdomainCatchall = (domainId: string, name: string) => {
    return useAxios<never, SubdomainCatchAllResponse>(
      'GET',
      `/domain/${domainId}/subdomain/${name}/mail/catchall`,
      true
    )
  }

  const setSubdomainCatchall = (domainId: string, name: string, data: SetSubdomainCatchAllRequest) => {
    return useAxios<SetSubdomainCatchAllRequest, SubdomainCatchAllResponse>(
      'PATCH',
      `/domain/${domainId}/subdomain/${name}/mail/catchall`,
      true,
      data
    )
  }

  const disableSubdomainCatchall = (domainId: string, name: string) => {
    return useAxios<never, void>(
      'DELETE',
      `/domain/${domainId}/subdomain/${name}/mail/catchall`,
      true
    )
  }

  const deprovisionSubdomain = (domainId: string, name: string) => {
    return useAxios<never, void>(
      'DELETE',
      `/domain/${domainId}/subdomain/${name}/mail`,
      true
    )
  }

  return {
    listSubdomains,
    provisionSubdomain,
    getSubdomainDns,
    autoConfigureSubdomainDns,
    getSubdomainCatchall,
    setSubdomainCatchall,
    disableSubdomainCatchall,
    deprovisionSubdomain
  }
}

/**
 * Account-scoped, not domain-scoped: the send limit belongs to the customer
 * account, not to any one domain, which is why the card is rendered once rather
 * than per domain.
 *
 * The response schema is passed to useAxios for validation. That is not the
 * local pattern in this file, but the shape here is a wire contract the backend
 * documents and the card branches on, so a silent shape change should surface
 * rather than render a blank card.
 */
export function useMailSendLimit() {
  const getSendLimit = () =>
    useAxios<never, MailSendLimitStatus>(
      'GET',
      '/mail/send-limit',
      true,
      undefined,
      MailSendLimitStatus
    )

  const requestLift = (reason: string) =>
    useAxios<{ reason: string }, MailSendLimitStatus>(
      'POST',
      '/mail/send-limit/request',
      true,
      { reason },
      MailSendLimitStatus
    )

  return { getSendLimit, requestLift }
}
